import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { mrpService, type MrpRun, type MrpRequirementRow, type MrpProductionOrderShortage } from '@/services/mrpService';
import * as XLSX from 'xlsx';
import { formatDateIST } from '@/utils/format';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';
import { useWarehouseStore } from '@/stores/warehouseStore';

export type ShortageStatus = 'SUFFICIENT' | 'CRITICAL' | 'SHORT';
export type ShortageAction = 'NONE' | 'PRODUCE' | 'PURCHASE';

export interface EnrichedShortageRow extends MrpRequirementRow {
  shortage: number;
  status: ShortageStatus;
  action: ShortageAction;
  isOrderImmediate: boolean;
  daysUntilOrder: number;
}

function formatShortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(dateStr);
  }
}

export default function MaterialShortagePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);

  const selectedWarehouseId = useWarehouseStore((s) => s.selectedWarehouseId);
  const selectedWarehouseName = useWarehouseStore((s) => s.selectedWarehouseName);
  const activeWarehouseId = selectedWarehouseId && selectedWarehouseId !== 'ALL' ? selectedWarehouseId : undefined;

  const [runs, setRuns] = useState<MrpRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState(searchParams.get('runId') || '');
  const [requirements, setRequirements] = useState<MrpRequirementRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [orderShortages, setOrderShortages] = useState<MrpProductionOrderShortage[]>([]);
  const [showCoveredOrderLines, setShowCoveredOrderLines] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [actionFilter, setActionFilter] = useState<string>('ALL');
  const [itemTypeFilter, setItemTypeFilter] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch runs
  const runOptions: SearchableOption[] = useMemo(() => {
    return runs.map((run) => ({
      value: run.id,
      label: `${run.runNumber ? `${run.runNumber} - ` : ''}${formatDateIST(run.runDate)} (${run.planningHorizonDays}d)${run.warehouseName ? ` • ${run.warehouseName}` : ''}`
    }));
  }, [runs]);

  const statusOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: 'All Status' },
    { value: 'CRITICAL', label: 'Critical' },
    { value: 'SHORT', label: 'Short' },
    { value: 'SUFFICIENT', label: 'Sufficient' }
  ], []);

  useEffect(() => {
    mrpService
      .getRuns(activeWarehouseId ? { warehouseId: activeWarehouseId } : undefined)
      .then((data) => {
        if (data && data.length > 0) {
          setRuns(data);
          const currentId = searchParams.get('runId');
          // Prefer currentId if it exists in data, else choose first run with shortages/items or data[0]
          const runWithReqs = data.find((r) => (r.itemsCount || 0) > 0 || (r.shortagesCount || 0) > 0);
          const targetId = (currentId && data.some((r) => r.id === currentId))
            ? currentId
            : (runWithReqs ? runWithReqs.id : data[0].id);
          setSelectedRunId(targetId);
          setSearchParams({ runId: targetId });
        } else {
          setRuns([]);
          setSelectedRunId('');
          setRequirements([]);
        }
      })
      .catch((err) => {
        console.error('Failed to load runs:', err);
        setRuns([]);
        setSelectedRunId('');
        setRequirements([]);
      });
  }, [activeWarehouseId]);

  // Fetch requirements for selected run
  useEffect(() => {
    if (!selectedRunId) {
      setRequirements([]);
      return;
    }
    setLoading(true);
    mrpService
      .getRequirements(selectedRunId, activeWarehouseId)
      .then((data) => {
        setRequirements(data || []);
      })
      .catch((err) => {
        console.error('Failed to load requirements:', err);
        setRequirements([]);
      })
      .finally(() => setLoading(false));
  }, [selectedRunId, activeWarehouseId]);

  // Material position of the open production orders in the selected run
  useEffect(() => {
    if (!selectedRunId) {
      setOrderShortages([]);
      return;
    }
    let cancelled = false;
    mrpService
      .getProductionOrderShortages(selectedRunId)
      .then((data) => {
        if (!cancelled) setOrderShortages(data || []);
      })
      .catch(() => {
        if (!cancelled) setOrderShortages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRunId]);

  const orderShortageRows = useMemo(
    () => (showCoveredOrderLines ? orderShortages : orderShortages.filter((r) => r.shortageQty > 0)),
    [orderShortages, showCoveredOrderLines],
  );
  const atRiskOrderCount = useMemo(
    () => new Set(orderShortages.filter((r) => r.atRisk).map((r) => r.productionOrderId)).size,
    [orderShortages],
  );
  const shortOrderCount = useMemo(
    () => new Set(orderShortages.filter((r) => r.shortageQty > 0).map((r) => r.productionOrderId)).size,
    [orderShortages],
  );

  const selectedRun = useMemo(() => {
    return runs.find((r) => r.id === selectedRunId) || runs[0];
  }, [runs, selectedRunId]);

  const displayRunNumber = selectedRun?.runNumber || 'No MRP run';
  const displayRunDate = selectedRun?.runDate ? formatShortDate(selectedRun.runDate) : '—';

  // Process rows with all metrics
  const processedRows = useMemo<EnrichedShortageRow[]>(() => {
    return requirements.map((r) => {
      const gross = Number(r.grossRequirement) || 0;
      const avail = Number(r.stockOnHand) || 0;
      const scheduled = Number(r.stockOnOrder) || 0;
      const net = Number(r.netRequirement) || 0;
      const leadTime = Number(r.leadTimeDays) || 0;
      const purchaseRate = Number(r.purchaseRate) || 0;

      // Shortage is the net requirement calculated by the MRP run
      const shortage = net > 0 ? net : 0;

      const requiredDate = r.requiredDate || null;
      let orderByDate = r.orderByDate;
      if (!orderByDate && requiredDate) {
        const d = new Date(requiredDate);
        d.setDate(d.getDate() - leadTime);
        orderByDate = d.toISOString().slice(0, 10);
      }

      const daysUntilOrder = orderByDate
        ? Math.ceil((new Date(orderByDate).getTime() - new Date(today).getTime()) / 86400000)
        : null;

      // Critical: short and either out of stock or the order-by date is within a week (or past)
      let status: ShortageStatus = 'SUFFICIENT';
      if (shortage > 0) {
        status = avail <= 0 || (daysUntilOrder !== null && daysUntilOrder <= 7) ? 'CRITICAL' : 'SHORT';
      }

      // Order immediately once the order-by date has arrived
      const isOrderImmediate = shortage > 0 && daysUntilOrder !== null && daysUntilOrder <= 0;

      // Items with an active BOM are produced; everything else is purchased
      let action: ShortageAction = 'NONE';
      if (status !== 'SUFFICIENT') {
        action = r.hasBom ? 'PRODUCE' : 'PURCHASE';
      }

      return {
        ...r,
        itemCode: r.itemCode || '',
        itemType: (r.itemType || '').toUpperCase().replace(/_/g, ' '),
        grossRequirement: gross,
        stockOnHand: avail,
        stockOnOrder: scheduled,
        netRequirement: net,
        shortage,
        leadTimeDays: leadTime,
        purchaseRate,
        orderByDate,
        requiredDate,
        daysUntilOrder: daysUntilOrder ?? 0,
        isOrderImmediate,
        status,
        action,
      };
    });
  }, [requirements, today]);

  // Overall KPI Summary
  const summary = useMemo(() => {
    let critical = 0;
    let short = 0;
    let sufficient = 0;
    let orderImmediately = 0;
    let estPurchaseValue = 0;

    for (const row of processedRows) {
      if (row.status === 'CRITICAL') {
        critical++;
      } else if (row.status === 'SHORT') {
        short++;
      } else {
        sufficient++;
      }

      if (row.isOrderImmediate) {
        orderImmediately++;
      }

      if (row.shortage > 0 && row.action === 'PURCHASE') {
        estPurchaseValue += row.shortage * (row.purchaseRate || 0);
      }
    }

    return {
      total: processedRows.length,
      critical,
      short,
      sufficient,
      orderImmediately,
      estPurchaseValue,
    };
  }, [processedRows]);

  // Filtered rows for the table
  const filteredRows = useMemo(() => {
    return processedRows.filter((r) => {
      // Status filter
      if (statusFilter !== 'ALL' && r.status !== statusFilter) {
        return false;
      }

      // Action filter
      if (actionFilter !== 'ALL' && r.action !== actionFilter) {
        return false;
      }

      // Item Type filter
      if (itemTypeFilter !== 'ALL') {
        const typeNorm = (r.itemType || '').toUpperCase();
        if (!typeNorm.includes(itemTypeFilter.toUpperCase())) {
          return false;
        }
      }

      // Search term
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchName = r.itemName?.toLowerCase().includes(q);
        const matchCode = r.itemCode?.toLowerCase().includes(q);
        if (!matchName && !matchCode) return false;
      }

      return true;
    });
  }, [processedRows, statusFilter, actionFilter, itemTypeFilter, searchTerm]);

  // Excel Export
  const exportToExcel = useCallback(() => {
    if (filteredRows.length === 0) return;
    const exportData = filteredRows.map((r) => ({
      Item: r.itemName,
      'Item Code': r.itemCode || '',
      Type: r.itemType || '',
      'Gross Req': r.grossRequirement,
      Available: r.stockOnHand,
      'Net Req': r.netRequirement,
      Scheduled: r.stockOnOrder > 0 ? `+${r.stockOnOrder}` : '—',
      Shortage: r.shortage > 0 ? r.shortage : '—',
      'Lead Time': `${r.leadTimeDays} days`,
      'Order By': r.orderByDate ? `Order by ${formatShortDate(r.orderByDate)}` : '—',
      'Required By': r.requiredDate ? formatShortDate(r.requiredDate) : '—',
      Status: r.status === 'SUFFICIENT' ? 'Sufficient' : r.status === 'CRITICAL' ? 'Critical' : 'Short',
      Action: r.action === 'NONE' ? 'None' : r.action === 'PRODUCE' ? 'Produce' : 'Purchase',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Shortage Report');
    XLSX.writeFile(workbook, `Material-Shortage-Report-${displayRunNumber}.xlsx`);
    toast.success('Excel report exported successfully');
  }, [filteredRows, displayRunNumber, toast]);

  return (
    <AppLayout>
      <div className="p-6 md:p-8 bg-[#f8fafc]/50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Material Shortage Report</h1>
              {selectedWarehouseName && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200/80 shadow-xs">
                  <i className="ri-store-2-line text-sm text-indigo-600" />
                  <span>{selectedWarehouseName}</span>
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Run <span className="font-semibold text-indigo-600">{displayRunNumber}</span>
              <span className="text-slate-400 mx-2">—</span>
              <span className="text-slate-600 font-medium">{displayRunDate}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/manufacturing/mrp')}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap shadow-xs"
            >
              Back to MRP
            </button>
            <button
              onClick={() => navigate(`/manufacturing/mrp/purchase?runId=${selectedRunId}`)}
              className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap shadow-xs"
            >
              View Suggestions
            </button>
          </div>
        </div>
        {/* Filter bar */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center flex-wrap">
            <div className="w-64">
              <SearchableSelect
                value={selectedRunId}
                onChange={(val) => {
                  setSelectedRunId(val);
                  setSearchParams({ runId: val });
                }}
                options={runOptions}
                placeholder="Select MRP Run"
                searchPlaceholder="Search MRP Run..."
              />
            </div>

            <div className="w-40">
              <SearchableSelect
                value={statusFilter}
                onChange={(val) => setStatusFilter(val)}
                options={statusOptions}
                placeholder="All Status"
              />
            </div>

          {/* Action Filter */}
          <div className="relative">
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="appearance-none bg-white border border-slate-200 rounded-lg pl-3 pr-8 py-2 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
            >
              <option value="ALL">All Actions</option>
              <option value="NONE">None</option>
              <option value="PRODUCE">Produce</option>
              <option value="PURCHASE">Purchase</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-base" />
          </div>

          {/* Item Type Filter */}
          <div className="relative">
            <select
              value={itemTypeFilter}
              onChange={(e) => setItemTypeFilter(e.target.value)}
              className="appearance-none bg-white border border-slate-200 rounded-lg pl-3 pr-8 py-2 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
            >
              <option value="ALL">All Item Types</option>
              <option value="FINISHED GOOD">Finished Goods</option>
              <option value="RAW MATERIAL">Raw Materials</option>
              <option value="SEMI-FINISHED">Semi-Finished</option>
              <option value="CONSUMABLE">Consumables</option>
              <option value="PACKING">Packing</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-base" />
          </div>

          {/* Search Input */}
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              placeholder="Search item..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-800 placeholder-slate-400"
            />
          </div>

          {/* Export to Excel */}
          <button
            onClick={exportToExcel}
            disabled={filteredRows.length === 0}
            className="ml-auto flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-40 cursor-pointer whitespace-nowrap shadow-xs"
          >
            <i className="ri-download-2-line text-slate-500 text-base" />
            <span>Export to Excel</span>
          </button>
        </div>
      </div>

        {/* 6 KPI Metric Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {/* 1. Total Items */}
          <div className="bg-white border border-slate-200/90 rounded-xl p-3.5 shadow-xs flex flex-col justify-between">
            <div className="text-xs font-semibold text-slate-500">Total Items</div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{summary.total}</div>
          </div>

          {/* 2. Critical */}
          <div className="bg-white border border-rose-100 rounded-xl p-3.5 shadow-xs flex flex-col justify-between">
            <div className="text-xs font-semibold text-rose-500">Critical</div>
            <div className="text-2xl font-bold text-rose-600 mt-1">{summary.critical}</div>
          </div>

          {/* 3. Short */}
          <div className="bg-white border border-amber-100 rounded-xl p-3.5 shadow-xs flex flex-col justify-between">
            <div className="text-xs font-semibold text-amber-500">Short</div>
            <div className="text-2xl font-bold text-amber-600 mt-1">{summary.short}</div>
          </div>

          {/* 4. Sufficient */}
          <div className="bg-white border border-emerald-100 rounded-xl p-3.5 shadow-xs flex flex-col justify-between">
            <div className="text-xs font-semibold text-emerald-600">Sufficient</div>
            <div className="text-2xl font-bold text-emerald-600 mt-1">{summary.sufficient}</div>
          </div>

          {/* 5. Est. Purchase Value */}
          <div className="bg-indigo-50/70 border border-indigo-100 rounded-xl p-3.5 shadow-xs flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-sm shrink-0 border border-indigo-200/50">
              ₹
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold text-indigo-700">Est. Purchase Value</div>
              <div className="text-lg sm:text-xl font-bold text-indigo-700 mt-0.5 truncate">
                ₹{summary.estPurchaseValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {/* 6. Order Immediately */}
          <div className="bg-white border border-rose-100 rounded-xl p-3.5 shadow-xs flex flex-col justify-between">
            <div className="text-xs font-semibold text-rose-500">Order Immediately</div>
            <div className="text-2xl font-bold text-rose-600 mt-1">{summary.orderImmediately}</div>
          </div>
        </div>

        {/* Requirements Table */}
        <div className="bg-white border border-slate-200/90 rounded-xl overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  <th className="py-3 px-3.5 text-left whitespace-nowrap min-w-[160px]">Item</th>
                  <th className="py-3 px-3 text-left whitespace-nowrap min-w-[110px]">Type</th>
                  <th className="py-3 px-3 text-right whitespace-nowrap min-w-[85px]">Gross Req</th>
                  <th className="py-3 px-3 text-right whitespace-nowrap min-w-[80px]">Available</th>
                  <th className="py-3 px-3 text-right whitespace-nowrap min-w-[80px]">Net Req</th>
                  <th className="py-3 px-3 text-center whitespace-nowrap min-w-[90px]">Scheduled</th>
                  <th className="py-3 px-3 text-center whitespace-nowrap min-w-[80px]">Shortage</th>
                  <th className="py-3 px-3 text-left whitespace-nowrap min-w-[90px]">Lead Time</th>
                  <th className="py-3 px-3 text-left whitespace-nowrap min-w-[150px]">Order By</th>
                  <th className="py-3 px-3 text-left whitespace-nowrap min-w-[110px]">Required By</th>
                  <th className="py-3 px-3 text-center whitespace-nowrap min-w-[110px]">Status</th>
                  <th className="py-3 px-3 text-center whitespace-nowrap min-w-[95px]">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredRows.map((row) => (
                  <tr key={row.itemId} className="hover:bg-slate-50/80 transition-colors">
                    {/* Item */}
                    <td className="py-3 px-3.5">
                      <div className="font-medium text-slate-900 leading-tight">{row.itemName}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{row.itemCode}</div>
                    </td>

                    {/* Type */}
                    <td className="py-3 px-3">
                      <span className="text-xs text-slate-500 font-semibold tracking-wide uppercase">
                        {row.itemType}
                      </span>
                    </td>

                    {/* Gross Req */}
                    <td className="py-3 px-3 text-right font-medium text-slate-900">
                      {row.grossRequirement}
                    </td>

                    {/* Available */}
                    <td className="py-3 px-3 text-right text-slate-700">
                      {row.stockOnHand}
                    </td>

                    {/* Net Req */}
                    <td className="py-3 px-3 text-right font-medium text-slate-900">
                      {row.netRequirement}
                    </td>

                    {/* Scheduled */}
                    <td className="py-3 px-3 text-center">
                      {row.stockOnOrder > 0 ? (
                        <span className="font-semibold text-emerald-600">+{row.stockOnOrder}</span>
                      ) : (
                        <span className="text-slate-400 font-normal">—</span>
                      )}
                    </td>

                    {/* Shortage */}
                    <td className="py-3 px-3 text-center">
                      {row.shortage > 0 ? (
                        <span className="font-bold text-rose-600">{row.shortage}</span>
                      ) : (
                        <span className="text-slate-400 font-normal">—</span>
                      )}
                    </td>

                    {/* Lead Time */}
                    <td className="py-3 px-3 text-slate-600 whitespace-nowrap">
                      {row.leadTimeDays} days
                    </td>

                    {/* Order By */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      {row.orderByDate ? (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            row.isOrderImmediate
                              ? 'bg-rose-50 text-rose-700 border-rose-200'
                              : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          }`}
                        >
                          Order by {formatShortDate(row.orderByDate)}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>

                    {/* Required By */}
                    <td className="py-3 px-3 text-slate-700 whitespace-nowrap">
                      {row.requiredDate ? formatShortDate(row.requiredDate) : '—'}
                    </td>

                    {/* Status */}
                    <td className="py-3 px-3 text-center whitespace-nowrap">
                      {row.status === 'SUFFICIENT' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <i className="ri-check-line text-xs font-bold" />
                          Sufficient
                        </span>
                      ) : row.status === 'CRITICAL' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                          <i className="ri-close-circle-line text-xs font-bold" />
                          Critical
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                          <i className="ri-alert-line text-xs font-bold" />
                          Short
                        </span>
                      )}
                    </td>

                    {/* Action */}
                    <td className="py-3 px-3 text-center whitespace-nowrap">
                      {row.action === 'NONE' ? (
                        <span className="text-xs text-slate-400 font-medium">None</span>
                      ) : row.action === 'PRODUCE' ? (
                        <button
                          onClick={() => navigate(`/manufacturing/mrp/purchase?runId=${selectedRunId}&tab=production`)}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors cursor-pointer"
                        >
                          Produce
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate(`/manufacturing/mrp/purchase?runId=${selectedRunId}`)}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors cursor-pointer"
                        >
                          Purchase
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {!loading && filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={12} className="px-4 py-14 text-center text-slate-400">
                      <div className="flex flex-col items-center gap-2">
                        {runs.length === 0 ? (
                          <>
                            <div className="w-12 h-12 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-2xl mb-1">
                              <i className="ri-store-2-line" />
                            </div>
                            <p className="font-semibold text-slate-800 text-base">No MRP Runs for {selectedWarehouseName || 'This Warehouse'}</p>
                            <p className="text-xs text-slate-500 max-w-sm">
                              No planning runs have been executed for this warehouse yet. Run MRP to identify material shortages.
                            </p>
                            <button
                              onClick={() => navigate('/manufacturing/mrp')}
                              className="mt-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg text-xs transition-colors shadow-xs cursor-pointer"
                            >
                              Go to Run MRP
                            </button>
                          </>
                        ) : requirements.length === 0 ? (
                          <>
                            <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center text-2xl mb-1">
                              <i className="ri-checkbox-circle-line" />
                            </div>
                            <p className="font-semibold text-slate-800 text-base">No Material Shortages Detected</p>
                            <p className="text-xs text-slate-500 max-w-sm">
                              All material requirements for {selectedWarehouseName || 'the warehouse'} are satisfied by stock on hand and scheduled receipts.
                            </p>
                          </>
                        ) : (
                          <>
                            <i className="ri-inbox-line text-3xl text-slate-300" />
                            <p className="font-medium text-slate-600">No items match the current filters</p>
                            <p className="text-xs text-slate-400">Try adjusting your search or filter criteria</p>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Production Order Material Shortage */}
        {orderShortages.length > 0 && (
          <div className="bg-white border border-slate-200/90 rounded-xl overflow-hidden shadow-xs mt-5">
            <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold text-slate-900">Production Order Material Shortage</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Material still to be issued to each open production order, against stock and incoming supply.
                  {' '}{shortOrderCount} order(s) short{atRiskOrderCount > 0 ? ` · ${atRiskOrderCount} already in progress and at risk of stopping` : ''}.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showCoveredOrderLines}
                  onChange={(e) => setShowCoveredOrderLines(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Show covered materials too
              </label>
            </div>
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    <th className="py-2.5 px-3.5 whitespace-nowrap">Production Order</th>
                    <th className="py-2.5 px-3.5 whitespace-nowrap">Material</th>
                    <th className="py-2.5 px-3.5 text-right whitespace-nowrap">Required</th>
                    <th className="py-2.5 px-3.5 text-right whitespace-nowrap">Issued</th>
                    <th className="py-2.5 px-3.5 text-right whitespace-nowrap">Still Needed</th>
                    <th className="py-2.5 px-3.5 text-right whitespace-nowrap">Covered</th>
                    <th className="py-2.5 px-3.5 text-right whitespace-nowrap">Shortage</th>
                    <th className="py-2.5 px-3.5 whitespace-nowrap">Order By</th>
                    <th className="py-2.5 px-3.5 whitespace-nowrap">Risk</th>
                    <th className="py-2.5 px-3.5 text-right whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {orderShortageRows.map((r) => (
                    <tr key={r.id} className={r.atRisk ? 'bg-rose-50/50' : 'hover:bg-slate-50/60'}>
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/manufacturing/production-orders/${r.productionOrderId}`)}
                          className="font-semibold text-indigo-600 hover:underline cursor-pointer"
                        >
                          {r.poNumber}
                        </button>
                        <div className="text-xs text-slate-500">
                          {r.productName || '—'} × {r.orderQty} · {r.orderStatus === 'CONFIRMED' ? 'PLANNED' : r.orderStatus.replace('_', ' ')}
                        </div>
                      </td>
                      <td className="py-2.5 px-3.5">
                        <div className="font-medium text-slate-800">{r.itemName}</div>
                        {r.itemCode && <div className="text-xs text-slate-400">{r.itemCode}</div>}
                      </td>
                      <td className="py-2.5 px-3.5 text-right text-slate-600">{r.requiredQty > 0 ? r.requiredQty : '—'}</td>
                      <td className="py-2.5 px-3.5 text-right text-slate-600">{r.requiredQty > 0 ? r.issuedQty : '—'}</td>
                      <td className="py-2.5 px-3.5 text-right font-medium text-slate-800">{r.outstandingQty} {r.unit}</td>
                      <td className="py-2.5 px-3.5 text-right text-emerald-700">{r.coveredQty}</td>
                      <td className={`py-2.5 px-3.5 text-right font-bold ${r.shortageQty > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {r.shortageQty > 0 ? `${r.shortageQty} ${r.unit}` : '0'}
                      </td>
                      <td className="py-2.5 px-3.5 whitespace-nowrap text-slate-600">
                        {r.shortageQty > 0 ? formatShortDate(r.orderByDate) : '—'}
                      </td>
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        {r.atRisk ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700 border border-rose-200" title="Order is in progress and this material is not available — production will stop">
                            <i className="ri-alarm-warning-line" /> Active · at risk
                          </span>
                        ) : r.shortageQty > 0 ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">Short</span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">Covered</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3.5 text-right whitespace-nowrap">
                        {r.shortageQty > 0 && (
                          <button
                            onClick={() => navigate(`/manufacturing/mrp/purchase?runId=${selectedRunId}`)}
                            className="px-2.5 py-1 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md cursor-pointer"
                          >
                            Create PO
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {orderShortageRows.length === 0 && (
                    <tr>
                      <td colSpan={10} className="py-8 text-center text-sm text-slate-500">
                        Every open production order has its remaining material covered by stock or incoming supply.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
